import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, DownloadIcon, FileTextIcon, HeadphonesIcon, SparklesIcon, XIcon } from 'lucide-react';
import { atnaQuestions, consents, invoices, policyDocs } from '../../data/profil';
import type { PanelId } from './AccountNav';
const titles: Record<PanelId, string> = {
  consentements: 'Consentements',
  factures: 'Mes factures',
  confidentialite: 'Confidentialité et transparence',
  atna: 'Aide ATNA'
};
function ConsentsPanel() {
  return <ul className="space-y-3" data-id="element-1816" data-mp="31473pjmk7vt1">
      {consents.map(consent => <li key={consent.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-1817" data-mp="2x0r90su1w5iz">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1818" data-mp="2s38ywz4ark9e">
            <h3 className="text-[15px] font-bold text-ville-navy" data-id="element-1819" data-mp="1lmnffnrdkjeg">{consent.title}</h3>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${consent.status === 'accorde' ? 'bg-ville-indigo-soft text-ville-indigo' : 'bg-amber-50 text-amber-800'}`} data-id="element-1820" data-mp="1lkaowm7jx0ps">
              {consent.status === 'accorde' ? 'Autorisé' : 'À confirmer'}
            </span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-slate-600" data-id="element-1821" data-mp="2380v42o2p76f">{consent.purpose}</p>
          <p className="mt-2 text-xs text-slate-500" data-id="element-1822" data-mp="285tzcq5tfr42">{consent.grantedOn}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-1823" data-mp="1thfxahg846ok">
            <button type="button" className="text-sm font-bold text-ville-indigo underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1824" data-mp="1y9qx7kayrsh4">
              Voir les détails
            </button>
            {consent.withdrawable ? <button type="button" className="text-sm font-semibold text-slate-500 underline underline-offset-4 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1825" data-mp="1ako85re02xcl">
                Retirer mon consentement
              </button> : <button type="button" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-4 py-2 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1826" data-mp="3cf2gwcqk8mb6">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1827" data-mp="tha539qmlz" />
                Confirmer
              </button>}
          </div>
        </li>)}
    </ul>;
}
function InvoicesPanel() {
  return <ul className="space-y-3" data-id="element-1828" data-mp="3m6gkavlmu69l">
      {invoices.map(invoice => <li key={invoice.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-1829" data-mp="2fr66xqpmop8x">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1830" data-mp="2as348cx4ev44">
            <div className="min-w-0" data-id="element-1831" data-mp="2au5yj95b3aen">
              <p className="font-bold text-ville-navy" data-id="element-1832" data-mp="20oo1ruo4qcl7">{invoice.service}</p>
              <p className="text-sm text-slate-500" data-id="element-1833" data-mp="25mps90nvxe6p">
                {invoice.date} · {invoice.reference}
              </p>
            </div>
            <div className="text-right" data-id="element-1834" data-mp="2alakacszgrk2">
              <p className="text-lg font-extrabold text-ville-navy" data-id="element-1835" data-mp="2fcsjowfcyk2x">{invoice.amount}</p>
              <span className={`mt-1 inline-block rounded-full px-3 py-1 text-xs font-bold ${invoice.status === 'payee' ? 'bg-slate-100 text-slate-600' : 'bg-ville-indigo-soft text-ville-indigo'}`} data-id="element-1836" data-mp="2fevgbzb0nbgm">
                {invoice.status === 'payee' ? 'Payée' : 'À payer'}
              </span>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-1837" data-mp="2yavclmb11160">
            <button type="button" className="text-sm font-bold text-ville-indigo underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1838" data-mp="yzu8utud5hge">
              Voir la facture
            </button>
            <button type="button" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1839" data-mp="2pgftnj4znzjb">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1840" data-mp="2yehyk9rufcbv" />
              Télécharger
            </button>
          </div>
        </li>)}
    </ul>;
}
function PrivacyPanel() {
  return <ul className="space-y-3" data-id="element-1841" data-mp="26nbslcpfykeo">
      {policyDocs.map(doc => <li key={doc.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-1842" data-mp="2ki4gayfhj6wc">
          <div className="flex items-start gap-4" data-id="element-1843" data-mp="2fos5ps7n50ml">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-ville-navy" aria-hidden="true" data-id="element-1844" data-mp="2fo0708iqyms3">
              <FileTextIcon className="h-5 w-5" data-id="element-1845" data-mp="1n1p6syz1ynvt" />
            </span>
            <div className="min-w-0" data-id="element-1846" data-mp="1mzbdauk0tjqd">
              <h3 className="text-[15px] font-bold text-ville-navy" data-id="element-1847" data-mp="2ivb656of1nh">{doc.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600" data-id="element-1848" data-mp="7ad9sml88q5a">{doc.description}</p>
              <a href="#profil" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline" data-id="element-1849" data-mp="3po2wdetn26dl">
                Consulter
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1850" data-mp="m6kpb9lox3e8" />
              </a>
            </div>
          </div>
        </li>)}
    </ul>;
}
function AtnaPanel() {
  return <div className="space-y-6" data-id="element-1851" data-mp="c9g3javrb9xy">
      <div className="flex items-start gap-3 rounded-2xl bg-ville-indigo-soft p-5" data-id="element-1852" data-mp="c851pglvgc9h">
        <SparklesIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-1853" data-mp="h6yf7915s39n" />
        <p className="text-sm leading-relaxed text-ville-navy" data-id="element-1854" data-mp="2wyvz0j5x7mtx">
          ATNA répond à vos questions sur votre compte, vos données et vos services. Ses réponses sont supervisées par
          la Ville et ne remplacent jamais un contact humain.
        </p>
      </div>

      <div data-id="element-1855" data-mp="2duiw1slp9ex6">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500" data-id="element-1856" data-mp="2du0ohztl7e9y">Questions fréquentes</h3>
        <ul className="mt-3 space-y-2" data-id="element-1857" data-mp="2930rkukgww7g">
          {atnaQuestions.map(question => <li key={question} data-id="element-1858" data-mp="244zc0jm93f0q">
              <button type="button" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1859" data-mp="243xwa1uai4co">
                {question}
              </button>
            </li>)}
        </ul>
      </div>

      <div className="rounded-2xl border border-slate-200 p-5" data-id="element-1860" data-mp="3kipejlxq1v89">
        <div className="flex items-start gap-3" data-id="element-1861" data-mp="3kgwhmycwupdg">
          <HeadphonesIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-navy" aria-hidden="true" data-id="element-1862" data-mp="3fjmmd22ubjhp" />
          <p className="text-sm leading-relaxed text-slate-600" data-id="element-1863" data-mp="3n46wem9hw9tn">
            Vous préférez parler à une personne ? Un agent vie privée peut vous répondre du lundi au vendredi.
          </p>
        </div>
        <button type="button" className="mt-4 inline-flex items-center justify-center rounded-full border border-ville-navy px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1864" data-mp="nkmxr4igbbl">
          Contacter un agent
        </button>
      </div>
    </div>;
}
const panels: Record<PanelId, () => JSX.Element> = {
  consentements: ConsentsPanel,
  factures: InvoicesPanel,
  confidentialite: PrivacyPanel,
  atna: AtnaPanel
};
interface AccountPanelProps {
  panel: PanelId | null;
  onClose: () => void;
}
export function AccountPanel({
  panel,
  onClose
}: AccountPanelProps) {
  const Content = panel ? panels[panel] : null;
  return <AnimatePresence data-id="element-1865" data-mp="1jg53q3gpctj4">
      {panel && Content && <motion.div className="fixed inset-0 z-50 flex justify-end" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1866" data-mp="1eh2bjjltmhsk">
          <button type="button" aria-label="Fermer le panneau" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-1867" data-mp="1ocn83284epux" />
          <motion.aside role="dialog" aria-modal="true" aria-label={titles[panel]} initial={{
        x: 40,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 40,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white shadow-2xl" data-id="element-1868" data-mp="1x6ihimg9z2gd">
            <header className="sticky top-0 flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-5" data-id="element-1869" data-mp="2oejvjqx7h3bk">
              <h2 className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1870" data-mp="2tdwn2oz0uykh">{titles[panel]}</h2>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1871" data-mp="2enpcevf9ajuc">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1872" data-mp="1vqefvil0e4il" />
              </button>
            </header>
            <div className="px-6 py-6" data-id="element-1873" data-mp="20o65g5y6lejm">
              <Content data-id="element-1874" data-mp="20p7oawlfv5bw" />
            </div>
          </motion.aside>
        </motion.div>}
    </AnimatePresence>;
}